import { errorFromResponse, HttpError, toHttpError } from './errors';
import type { ApiPath, Body, Method, PathParams, PendingPath, Res, UnbuiltShape } from './wire';
import { EP } from './wire';
export type QueryValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | readonly (string | number)[];
type ParamsArg<P extends ApiPath | PendingPath, M extends Method> = [PathParams<P, M>] extends [
  never,
]
  ? { params?: never }
  : { params: PathParams<P, M> };
export type ReadInit<P extends ApiPath | PendingPath, M extends Method> = ParamsArg<P, M> & {
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  /**
   * 显式指定这次请求用的 bearer —— **覆盖**内存里那枚（`undefined` = 用内存里的）。
   *
   * 为什么需要：后端 `/auth/refresh` **要求请求带 `Authorization: Bearer …` 头**
   * （`backend/src/auth/router.py::refresh_endpoint` —— 它故意不校验 access 的
   * 有效性，只要头在）。而「access 已过期 ⇒ 重新打开应用」这条路径上，内存里
   * 那枚 access **压根没被装载**（`AuthProvider` 只在 access 未过期时才装）⇒
   * 不显式带上就会 401 ⇒ 前端判「后端明确拒绝」⇒ 清场 ⇒ **用户被踢回登录页**。
   */
  bearer?: string | null;
};
export type WriteInit<P extends ApiPath | PendingPath, M extends Method> = ParamsArg<P, M> &
  BodyArg<P, M> & {
    query?: Record<string, QueryValue>;
    signal?: AbortSignal;
    /** 见 {@link ReadInit.bearer}。 */
    bearer?: string | null;
  };
type BodyArg<P extends ApiPath | PendingPath, M extends Method> = P extends PendingPath
  ? { body?: UnbuiltShape }
  : [Body<P, M>] extends [never]
    ? { body?: never }
    : { body: Body<P, M> };
type RequiredKeys<T> = {
  [K in keyof T]-?: Record<string, never> extends Pick<T, K> ? never : K;
}[keyof T];
type ReadArgs<P extends ApiPath | PendingPath, M extends Method> = [
  RequiredKeys<ReadInit<P, M>>,
] extends [never]
  ? [init?: ReadInit<P, M>]
  : [init: ReadInit<P, M>];
let accessToken: string | null = null;
export function setAccessToken(token: string | null): void {
  accessToken = token;
}
let unauthorizedHandler: (() => Promise<boolean>) | null = null;
export function setUnauthorizedHandler(handler: (() => Promise<boolean>) | null): void {
  unauthorizedHandler = handler;
}
function buildPath(path: string, params: Record<string, string> | undefined): string {
  if (!params) return path;
  let url: string = path;
  for (const [key, value] of Object.entries(params)) {
    if (!url.includes(`{${key}}`)) {
      throw new Error(`endpoint ${path} has no {${key}} placeholder`);
    }
    url = url.replaceAll(`{${key}}`, encodeURIComponent(value));
  }
  if (/\{[A-Za-z_]+\}/.test(url)) {
    throw new Error(`endpoint ${path} has unfilled path params: ${url}`);
  }
  return url;
}
function buildUrl(
  path: string,
  params: Record<string, string> | undefined,
  query: Record<string, QueryValue> | undefined,
): string {
  const target = buildPath(path, params);
  if (!query) return target;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
      continue;
    }
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs === '' ? target : `${target}?${qs}`;
}
function authHeaders(hasBody: boolean, bearer: string | null | undefined): Record<string, string> {
  const headers: Record<string, string> = {};
  if (hasBody) headers['Content-Type'] = 'application/json';
  // `undefined` = 用内存里那枚；`null` = 这次**明确不带** bearer；
  // 字符串 = 这次就用它（哪怕内存里没有）。
  const token = bearer === undefined ? accessToken : bearer;
  if (token !== null && token !== '') headers.Authorization = `Bearer ${token}`;
  return headers;
}
type CallInit<P extends ApiPath | PendingPath, M extends Method> = {
  params?: PathParams<P, M>;
  query?: Record<string, QueryValue>;
  body?: unknown;
  signal?: AbortSignal;
  bearer?: string | null;
};

async function callOnce<P extends ApiPath | PendingPath, M extends Method>(
  path: P,
  method: M,
  init: CallInit<P, M>,
  url: string,
  retried = false,
): Promise<Res<P, M>> {
  let response: Response;
  try {
    const requestInit: RequestInit = {
      method,
      headers: authHeaders(init.body !== undefined, init.bearer),
    };
    if (init.body !== undefined) requestInit.body = JSON.stringify(init.body);
    if (init.signal !== undefined) requestInit.signal = init.signal;
    response = await fetch(url, requestInit);
  } catch (cause) {
    if (cause instanceof Error && cause.name === 'AbortError') throw cause;
    throw toHttpError(cause);
  }
  if (!response.ok) {
    if (
      response.status === 401 &&
      !retried &&
      path !== EP.authRefresh &&
      unauthorizedHandler !== null
    ) {
      const renewed = await unauthorizedHandler();
      if (renewed) return callOnce(path, method, init, url, true);
    }
    const body: unknown = await response.json().catch(() => null);
    throw errorFromResponse(response.status, body);
  }
  if (response.status === 204) return undefined as Res<P, M>;
  const text = await response.text();
  if (text === '') return undefined as Res<P, M>;
  return unwrapEnvelope(JSON.parse(text)) as Res<P, M>;
}

/**
 * 统一 1.5s 节流（所有请求），键 = method + 完整 URL（**读**）/ 再加 body（**写**）。同一键在窗口内：
 * - 读请求（GET，以及搜索型 POST）：经典节流。首飞立即发出；窗口内的调用
 *   不吃首飞的旧值，而是等窗口末尾的一次补发（trailing）拿新数据，多个
 *   后来者共享同一次补发。否则「上一笔写请求 invalidate 触发的刷新」会
 *   永远拿到窗口起点前的旧列表。
 * - 写请求（其余 post/patch/put/delete）：后来者共享首飞的 Promise ——
 *   双击/重复提交只落一次网络请求，也共享同一次结果。
 * - 任一次飞行失败立即出窗：手动重试不被旧账卡住。
 * 窗口判定只看时间戳 —— 后台标签页 setTimeout 被浏览器限流到远超 1.5s 时，
 * 过期条目不会被误当有效（代价只是 GC 晚一点）。SSE 走 EventSource、现有
 * 轮询最小间隔 3s（NotificationCenter 30s / CdkeyBind 3s），均不经此处节流。
 *
 * ## 键为什么不带 body（读）与为什么带（写）
 *
 * **读**：节流的目的**是**「同一端点在一段时间内只发一次」，**不是**「同一组参数只发一次」。
 * 键里带 body ⇒ 列表页的「列表查询」与「统计查询」各成一条 ⇒ 同一窗口内同一端点
 * 会发多次（实测：SSE 建流触发的那轮全量失效，1s 内把 `orders/accounts/search`
 * 打了 3 次——每次都带**各自的 body**）。
 * ⇒ 读请求的键只到 URL；窗口内的后来者把**最新一次**的参数记进 `entry.latest`，
 * 窗口末尾的补发用**最新参数**发出去。
 *
 * ⚠️ **补发必须带最新参数**：否则「用户改了筛选 ⇒ 补发仍带旧筛选的 body」，
 * 响应落进**当前**查询键（`useQuery` 的 key 含筛选）⇒ 表格显示的是**另一个筛选**的
 * 数据，而筛选框写着当前筛选（2026-10-23 实测逮到）。
 *
 * **写**：键**必须**带 body —— 不同 body 的写请求**不能**合并（「删 A」与「删 B」
 * 并成一次请求 ⇒ 删 B 静默丢失）。写请求的「后来者共享首飞」语义只在**同 body**
 * 时才成立。
 */
const THROTTLE_MS = 1500;
/**
 * 读语义不只看 method：本树的列表/统计/查找端点全是 POST（以 search /
 * stats / lookup-user / grants / export-options 结尾）。它们由失效标签驱动
 * 重拉，若按写合并会吃首飞旧值，刷新白做；故按路径后缀归入读（补发语义）。
 * 不匹配后缀的 POST 一律按写 —— 宁可保守合并，不让重复提交漏网。
 */
const READ_POST_SUFFIX = /\/(search|stats|lookup-user|grants|export-options)$/;
function isReadCall(method: Method, url: string): boolean {
  if (method === 'get') return true;
  if (method !== 'post') return false;
  return READ_POST_SUFFIX.test(url.split('?')[0] ?? url);
}
type ThrottleEntry = {
  at: number;
  promise: Promise<unknown>;
  trailPromise?: Promise<unknown>;
  /**
   * 尾随补发要用的**最新一次**调用参数。
   *
   * 读语义下窗口内可能有**多次**调用（参数不同：改了筛选、换了页码…）⇒
   * 补发必须用**最后一次**的参数，否则响应落进当前查询键时带着**另一个筛选**
   * 的数据（2026-10-23 实测逮到：表格显示无筛选的行、筛选框却写着筛选值）。
   */
  latest: CallArgs;
};
type CallArgs = {
  path: ApiPath | PendingPath;
  method: Method;
  init: CallInit<ApiPath | PendingPath, Method>;
  url: string;
};
const recentCalls = new Map<string, ThrottleEntry>();

function launch<P extends ApiPath | PendingPath, M extends Method>(
  key: string,
  path: P,
  method: M,
  init: CallInit<P, M>,
  url: string,
): Promise<Res<P, M>> {
  const promise: Promise<unknown> = callOnce(path, method, init, url);
  const entry: ThrottleEntry = {
    at: Date.now(),
    promise,
    latest: { path, method, init, url } as CallArgs,
  };
  recentCalls.set(key, entry);
  const evict = (): void => {
    if (recentCalls.get(key) === entry) recentCalls.delete(key);
  };
  setTimeout(evict, THROTTLE_MS);
  promise.catch(evict);
  return promise as Promise<Res<P, M>>;
}

function call<P extends ApiPath | PendingPath, M extends Method>(
  path: P,
  method: M,
  init: CallInit<P, M>,
): Promise<Res<P, M>> {
  const url = buildUrl(path, init.params as Record<string, string> | undefined, init.query);
  const read = isReadCall(method, url);
  // 读：键只到 URL（同端点的不同查询共用一个窗口 ⇒ 窗口内只发一次）。
  // 写：键带 body（不同 body 的写请求不能合并）。
  const key = read
    ? `${method} ${url}`
    : `${method} ${url} ${init.body === undefined ? '' : JSON.stringify(init.body)}`;
  const now = Date.now();
  const entry = recentCalls.get(key);
  if (entry === undefined || now - entry.at >= THROTTLE_MS) {
    return launch(key, path, method, init, url);
  }
  if (!read) return entry.promise as Promise<Res<P, M>>;
  // 读：窗口内 ⇒ 记下**最新**的参数（补发要用它）。
  entry.latest = { path, method, init, url } as CallArgs;
  if (entry.trailPromise === undefined) {
    let resolve!: (value: unknown) => void;
    let reject!: (reason: unknown) => void;
    entry.trailPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    const remaining = Math.max(THROTTLE_MS - (now - entry.at), 0);
    setTimeout(() => {
      // ⚠️ **用触发时刻的 `latest`**（不是创建 trailPromise 那次的参数）——
      // 窗口内用户又改了筛选/页码时，补发必须跟着走最新的那一份。
      const args = entry.latest;
      const fresh = launch(key, args.path, args.method, args.init, args.url);
      fresh.then(resolve, reject);
    }, remaining);
  }
  return entry.trailPromise as Promise<Res<P, M>>;
}

/**
 * pipe 的 2xx 响应是 `{code: "ok", data, message, request_id}` 信封；`Res` 类型
 * 指信封里的 data，这里做对应的运行时解包。backend 的裸返回没有这个形状，
 * 原样通过 —— 判定用三键齐全，避免误吞业务对象。
 */
function unwrapEnvelope(body: unknown): unknown {
  if (
    typeof body === 'object' &&
    body !== null &&
    'code' in body &&
    'data' in body &&
    'message' in body
  ) {
    return (body as { data: unknown }).data;
  }
  return body;
}
export function apiGet<P extends ApiPath | PendingPath>(
  path: P,
  ...args: ReadArgs<P, 'get'>
): Promise<Res<P, 'get'>> {
  return call(path, 'get', (args[0] ?? {}) as ReadInit<P, 'get'>);
}
export function apiPost<P extends ApiPath | PendingPath>(
  path: P,
  init: WriteInit<P, 'post'>,
): Promise<Res<P, 'post'>> {
  return call(path, 'post', init);
}
export function apiPatch<P extends ApiPath | PendingPath>(
  path: P,
  init: WriteInit<P, 'patch'>,
): Promise<Res<P, 'patch'>> {
  return call(path, 'patch', init);
}
export function apiDelete<P extends ApiPath | PendingPath>(
  path: P,
  ...args: ReadArgs<P, 'delete'>
): Promise<Res<P, 'delete'>> {
  return call(path, 'delete', (args[0] ?? {}) as ReadInit<P, 'delete'>);
}
export { HttpError };
